// backgroundPatterns/patternGenerators.js
//
// Parametric SVG background patterns. Each generator is a PURE function:
//   (params: { scale, fg, bg, weight? }) -> raw SVG markup string (one tile).
//
// The tile is meant to be tiled (background-repeat: repeat) via a CSS
// `background-image: url("data:image/svg+xml,...")` value — see ./toBackgroundCss.
// No network, no React, no side effects: these render instantly and fit the
// existing `bg_wallpaper` CSS-string data model (currentBg === 3 / setBgGradient).
//
// Design notes:
// - `scale` is the tile size in px (pattern "size up/down"). Larger scale = bigger
//   motif. Clamped by the caller to SCALE_MIN..SCALE_MAX.
// - `fg` / `bg` are CSS colors (hex). Every pattern paints a full `bg` rect first
//   so the tile is opaque and seamless; `fg` draws the motif.
// - `weight` (0..1) controls stroke/line thickness or dot radius where meaningful;
//   it has a sensible default so callers may omit it.
// - Each tile carries `data-iyuga-pattern="<id>"` so a stored data-URI is
//   self-describing and the descriptor can be recovered later (see ./descriptor).

export const SCALE_MIN = 8;
export const SCALE_MAX = 96;
export const SCALE_DEFAULT = 32;

export const DEFAULT_FG = "#123051";
export const DEFAULT_BG = "#ffffff";

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

// Conservative allow-list of color forms safe to embed in SVG attributes AND in
// a raw CSS `background:` string. Rejects anything carrying CSS/markup breakout
// characters (`;`/`{`/`}`/`<`/`>`/`(`/`)` outside an allowed function) or
// arbitrary text. Accepts: #hex (3/4/6/8) and rgb()/rgba()/hsl()/hsla() built
// only from digits, %, ., commas, spaces, slashes.
const SAFE_COLOR_RE =
  /^(?:#[0-9a-fA-F]{3,8}|(?:rgb|rgba|hsl|hsla)\(\s*[0-9.,%\s/]+\))$/;

// True for a string color that is safe to interpolate untrusted into output.
export const isSafeColor = (c) =>
  typeof c === "string" && SAFE_COLOR_RE.test(c.trim());

// Return the color if safe, else the supplied fallback. Trims surrounding ws.
export const safeColor = (c, fallback) =>
  isSafeColor(c) ? c.trim() : fallback;

// Round to 3 decimals to keep the data-URI compact and deterministic.
const r = (n) => Math.round(n * 1000) / 1000;

const svgOpen = (id, w, h) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" ` +
  `viewBox="0 0 ${w} ${h}" data-iyuga-pattern="${id}">`;

const bgRect = (w, h, bg) => `<rect width="${w}" height="${h}" fill="${bg}"/>`;

const normParams = (p) => {
  // `p` may be null/undefined or carry NaN/non-finite values from callers.
  // Coerce to safe finite numbers BEFORE clamp — clamp(NaN,...) returns NaN,
  // which would emit invalid attributes like r="NaN".
  const src = p || {};
  const scaleNum = Number(src.scale);
  const scale = clamp(
    Number.isFinite(scaleNum) && scaleNum !== 0 ? scaleNum : SCALE_DEFAULT,
    SCALE_MIN,
    SCALE_MAX
  );
  // Colors are interpolated raw into SVG fill/stroke attributes (and later into
  // a CSS string), so validate against the safe-color allow-list and fall back
  // to defaults on anything that could break out / inject.
  const fg = safeColor(src.fg, DEFAULT_FG);
  const bg = safeColor(src.bg, DEFAULT_BG);
  // weight default 0.5 -> a balanced look across patterns.
  const weightNum = Number(src.weight);
  const weight = clamp(Number.isFinite(weightNum) ? weightNum : 0.5, 0.05, 1);
  return { scale, fg, bg, weight };
};

// ---- generators ------------------------------------------------------------

const checkerboard = (p) => {
  const { scale: s, fg, bg } = normParams(p);
  const h = r(s / 2);
  return (
    svgOpen("checkerboard", s, s) +
    bgRect(s, s, bg) +
    `<rect width="${h}" height="${h}" fill="${fg}"/>` +
    `<rect x="${h}" y="${h}" width="${h}" height="${h}" fill="${fg}"/>` +
    `</svg>`
  );
};

const dots = (p) => {
  // Polka dots: a single centered dot per tile (tiling fills the field).
  const { scale: s, fg, bg, weight } = normParams(p);
  const c = r(s / 2);
  const radius = r((s / 2) * (0.18 + weight * 0.32)); // ~0.18..0.5 of half-tile
  return (
    svgOpen("dots", s, s) +
    bgRect(s, s, bg) +
    `<circle cx="${c}" cy="${c}" r="${radius}" fill="${fg}"/>` +
    `</svg>`
  );
};

const stripes = (p) => {
  // Diagonal stripes (45deg). Two bands per tile so it tiles seamlessly.
  const { scale: s, fg, bg, weight } = normParams(p);
  const band = r(s * (0.2 + weight * 0.3)); // stripe thickness
  return (
    svgOpen("stripes", s, s) +
    bgRect(s, s, bg) +
    `<path d="M0 ${s} L${s} 0 M${r(-s)} ${r(s / 2)} L${r(s / 2)} ${r(-s)} ` +
    `M${r(s / 2)} ${r(s + s / 2)} L${r(s + s / 2)} ${r(s / 2)}" ` +
    `stroke="${fg}" stroke-width="${band}"/>` +
    `</svg>`
  );
};

const verticalStripes = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const w = r(s * (0.25 + weight * 0.25));
  return (
    svgOpen("verticalStripes", s, s) +
    bgRect(s, s, bg) +
    `<rect width="${w}" height="${s}" fill="${fg}"/>` +
    `</svg>`
  );
};

const grid = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(0.5 + weight * 2.5); // 0.5..3 px lines
  return (
    svgOpen("grid", s, s) +
    bgRect(s, s, bg) +
    `<path d="M${s} 0 L0 0 0 ${s}" fill="none" stroke="${fg}" stroke-width="${sw}"/>` +
    `</svg>`
  );
};

const zigzag = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(1 + weight * 4);
  const h = r(s / 2);
  // A chevron/zigzag line that meets at the tile edges so it tiles horizontally.
  return (
    svgOpen("zigzag", s, s) +
    bgRect(s, s, bg) +
    `<path d="M0 ${h} L${h} 0 L${s} ${h}" fill="none" stroke="${fg}" ` +
    `stroke-width="${sw}" stroke-linejoin="round" stroke-linecap="round"/>` +
    `<path d="M0 ${s} L${h} ${h} L${s} ${s}" fill="none" stroke="${fg}" ` +
    `stroke-width="${sw}" stroke-linejoin="round" stroke-linecap="round"/>` +
    `</svg>`
  );
};

const chevron = (p) => {
  // Filled chevron bands.
  const { scale: s, fg, bg } = normParams(p);
  const h = r(s / 2);
  const q = r(s / 4);
  return (
    svgOpen("chevron", s, s) +
    bgRect(s, s, bg) +
    `<path d="M0 ${q} L${h} ${r(-q)} L${s} ${q} L${s} ${h} L${h} ${q} L0 ${h} Z" fill="${fg}"/>` +
    `<path d="M0 ${r(s + q)} L${h} ${q} L${s} ${r(s + q)} L${s} ${s} L${h} ${r(s - q)} L0 ${s} Z" fill="${fg}"/>` +
    `</svg>`
  );
};

const triangles = (p) => {
  const { scale: s, fg, bg } = normParams(p);
  const h = r(s / 2);
  return (
    svgOpen("triangles", s, s) +
    bgRect(s, s, bg) +
    `<path d="M0 ${s} L${h} 0 L${s} ${s} Z" fill="${fg}"/>` +
    `</svg>`
  );
};

const crosshatch = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(0.5 + weight * 2);
  return (
    svgOpen("crosshatch", s, s) +
    bgRect(s, s, bg) +
    `<path d="M0 0 L${s} ${s} M0 ${s} L${s} 0" stroke="${fg}" ` +
    `stroke-width="${sw}" fill="none"/>` +
    `</svg>`
  );
};

const plus = (p) => {
  // Plus / cross grid.
  const { scale: s, fg, bg, weight } = normParams(p);
  const c = r(s / 2);
  const arm = r(s * (0.18 + weight * 0.22));
  const t = r(s * (0.06 + weight * 0.1));
  return (
    svgOpen("plus", s, s) +
    bgRect(s, s, bg) +
    `<rect x="${r(c - t)}" y="${r(c - arm)}" width="${r(t * 2)}" height="${r(arm * 2)}" fill="${fg}"/>` +
    `<rect x="${r(c - arm)}" y="${r(c - t)}" width="${r(arm * 2)}" height="${r(t * 2)}" fill="${fg}"/>` +
    `</svg>`
  );
};

const waves = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(1 + weight * 3);
  const h = r(s / 2);
  const q = r(s / 4);
  return (
    svgOpen("waves", s, s) +
    bgRect(s, s, bg) +
    `<path d="M0 ${h} Q${q} 0 ${h} ${h} T${s} ${h}" fill="none" ` +
    `stroke="${fg}" stroke-width="${sw}"/>` +
    `</svg>`
  );
};

const diamonds = (p) => {
  const { scale: s, fg, bg } = normParams(p);
  const h = r(s / 2);
  return (
    svgOpen("diamonds", s, s) +
    bgRect(s, s, bg) +
    `<path d="M${h} 0 L${s} ${h} L${h} ${s} L0 ${h} Z" fill="${fg}"/>` +
    `</svg>`
  );
};

// ---- extended generators (ported from deck PATS[0..44], DL-063a) ------------
// Deck bakes ink #123051; here the motif paints with `fg` so every pattern is
// TINTABLE to the Segment/Wallpaper color by construction (deck caption intent).
// All coordinates are deterministic (no randomness) so output is stable/testable.

const gridFine = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(0.4 + weight * 1.6);
  const h = r(s / 2);
  return (
    svgOpen("gridFine", s, s) + bgRect(s, s, bg) +
    `<path d="M${h} 0 L${h} ${s} M0 ${h} L${s} ${h} M${s} 0 L0 0 0 ${s}" ` +
    `fill="none" stroke="${fg}" stroke-width="${sw}"/></svg>`
  );
};

const linesHorizontal = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const t = r(s * (0.16 + weight * 0.2));
  return (
    svgOpen("linesHorizontal", s, s) + bgRect(s, s, bg) +
    `<rect width="${s}" height="${t}" fill="${fg}"/></svg>`
  );
};

const diagonalReverse = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const band = r(s * (0.2 + weight * 0.3));
  return (
    svgOpen("diagonalReverse", s, s) + bgRect(s, s, bg) +
    `<path d="M0 0 L${s} ${s} M${r(-s / 2)} ${r(s / 2)} L${r(s / 2)} ${r(s + s / 2)} ` +
    `M${r(s / 2)} ${r(-s / 2)} L${r(s + s / 2)} ${r(s / 2)}" ` +
    `stroke="${fg}" stroke-width="${band}"/></svg>`
  );
};

const chevronDown = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(1 + weight * 4);
  const h = r(s / 2);
  return (
    svgOpen("chevronDown", s, s) + bgRect(s, s, bg) +
    `<path d="M0 0 L${h} ${h} L${s} 0" fill="none" stroke="${fg}" stroke-width="${sw}" stroke-linejoin="round"/>` +
    `<path d="M0 ${h} L${h} ${s} L${s} ${h}" fill="none" stroke="${fg}" stroke-width="${sw}" stroke-linejoin="round"/></svg>`
  );
};

const waveTall = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(1 + weight * 3);
  const h = r(s / 2);
  const q = r(s / 4);
  return (
    svgOpen("waveTall", s, s) + bgRect(s, s, bg) +
    `<path d="M0 ${q} Q${q} ${r(-q)} ${h} ${q} T${s} ${q}" fill="none" stroke="${fg}" stroke-width="${sw}"/>` +
    `<path d="M0 ${r(q + h)} Q${q} ${r(h - q)} ${h} ${r(q + h)} T${s} ${r(q + h)}" fill="none" stroke="${fg}" stroke-width="${sw}"/></svg>`
  );
};

const trianglesAlt = (p) => {
  const { scale: s, fg, bg } = normParams(p);
  const h = r(s / 2);
  return (
    svgOpen("trianglesAlt", s, s) + bgRect(s, s, bg) +
    `<path d="M0 0 L${h} ${s} L${s} 0 Z" fill="${fg}"/></svg>`
  );
};

const diamondsFilled = (p) => {
  const { scale: s, fg, bg } = normParams(p);
  const h = r(s / 2);
  return (
    svgOpen("diamondsFilled", s, s) + bgRect(s, s, bg) +
    `<path d="M${h} 0 L${s} ${h} L${h} ${s} L0 ${h} Z" fill="${fg}"/>` +
    `<path d="M0 0 L${h} 0 L0 ${h} Z M${s} 0 L${h} 0 L${s} ${h} Z M0 ${s} L${h} ${s} L0 ${h} Z M${s} ${s} L${h} ${s} L${s} ${h} Z" fill="${fg}" opacity="0.4"/></svg>`
  );
};

const hexagons = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(0.6 + weight * 2);
  const w = r(s / 2);
  const q = r(s / 4);
  const tq = r((s * 3) / 4);
  return (
    svgOpen("hexagons", s, s) + bgRect(s, s, bg) +
    `<path d="M${w} 0 L${s} ${q} L${s} ${tq} L${w} ${s} L0 ${tq} L0 ${q} Z" ` +
    `fill="none" stroke="${fg}" stroke-width="${sw}"/></svg>`
  );
};

const quatrefoil = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(0.6 + weight * 1.8);
  const c = r(s / 2);
  const a = r(s * 0.26);
  return (
    svgOpen("quatrefoil", s, s) + bgRect(s, s, bg) +
    `<path d="M${r(c - a)} ${c} A${a} ${a} 0 0 1 ${c} ${r(c - a)} A${a} ${a} 0 0 1 ${r(c + a)} ${c} ` +
    `A${a} ${a} 0 0 1 ${c} ${r(c + a)} A${a} ${a} 0 0 1 ${r(c - a)} ${c} Z" ` +
    `fill="none" stroke="${fg}" stroke-width="${sw}"/></svg>`
  );
};

const scales = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(0.6 + weight * 1.6);
  const h = r(s / 2);
  return (
    svgOpen("scales", s, s) + bgRect(s, s, bg) +
    `<path d="M0 ${h} A${h} ${h} 0 0 1 ${s} ${h}" fill="none" stroke="${fg}" stroke-width="${sw}"/>` +
    `<path d="M${r(-h)} ${s} A${h} ${h} 0 0 1 ${h} ${s}" fill="none" stroke="${fg}" stroke-width="${sw}"/>` +
    `<path d="M${h} ${s} A${h} ${h} 0 0 1 ${r(s + h)} ${s}" fill="none" stroke="${fg}" stroke-width="${sw}"/></svg>`
  );
};

const seigaiha = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(0.5 + weight * 1.2);
  const h = r(s / 2);
  let arcs = "";
  for (let k = 3; k >= 1; k--) {
    const rr = r((h * k) / 3);
    arcs += `<path d="M${r(h - rr)} ${h} A${rr} ${rr} 0 0 1 ${r(h + rr)} ${h}" fill="none" stroke="${fg}" stroke-width="${sw}"/>`;
  }
  return svgOpen("seigaiha", s, s) + bgRect(s, s, bg) + arcs + `</svg>`;
};

const arcs = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(1 + weight * 3);
  const h = r(s / 2);
  return (
    svgOpen("arcs", s, s) + bgRect(s, s, bg) +
    `<path d="M0 ${s} A${s} ${s} 0 0 1 ${s} ${s}" fill="none" stroke="${fg}" stroke-width="${sw}"/>` +
    `<path d="M0 ${h} A${h} ${h} 0 0 1 ${s} ${h}" fill="none" stroke="${fg}" stroke-width="${sw}"/></svg>`
  );
};

const crosses = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(1 + weight * 2.5);
  const c = r(s / 2);
  const a = r(s * (0.14 + weight * 0.16));
  return (
    svgOpen("crosses", s, s) + bgRect(s, s, bg) +
    `<path d="M${r(c - a)} ${r(c - a)} L${r(c + a)} ${r(c + a)} M${r(c - a)} ${r(c + a)} L${r(c + a)} ${r(c - a)}" ` +
    `stroke="${fg}" stroke-width="${sw}" stroke-linecap="round"/></svg>`
  );
};

const squares = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(0.6 + weight * 2);
  const m = r(s * 0.18);
  const sz = r(s - m * 2);
  return (
    svgOpen("squares", s, s) + bgRect(s, s, bg) +
    `<rect x="${m}" y="${m}" width="${sz}" height="${sz}" fill="none" stroke="${fg}" stroke-width="${sw}"/></svg>`
  );
};

const nestedSquares = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(0.5 + weight * 1.4);
  const c = r(s / 2);
  let inner = "";
  for (let k = 1; k <= 3; k++) {
    const half = r((s / 2) * (k / 3.6));
    inner += `<rect x="${r(c - half)}" y="${r(c - half)}" width="${r(half * 2)}" height="${r(half * 2)}" fill="none" stroke="${fg}" stroke-width="${sw}"/>`;
  }
  return svgOpen("nestedSquares", s, s) + bgRect(s, s, bg) + inner + `</svg>`;
};

const rings = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(0.6 + weight * 2);
  const c = r(s / 2);
  const rad = r(s * 0.32);
  return (
    svgOpen("rings", s, s) + bgRect(s, s, bg) +
    `<circle cx="${c}" cy="${c}" r="${rad}" fill="none" stroke="${fg}" stroke-width="${sw}"/></svg>`
  );
};

const stars = (p) => {
  const { scale: s, fg, bg } = normParams(p);
  const c = r(s / 2);
  const R = s * 0.34;
  const ri = R * 0.42;
  let pts = "";
  for (let k = 0; k < 10; k++) {
    const ang = (Math.PI / 5) * k - Math.PI / 2;
    const rad = k % 2 === 0 ? R : ri;
    pts += `${r(c + rad * Math.cos(ang))},${r(c + rad * Math.sin(ang))} `;
  }
  return (
    svgOpen("stars", s, s) + bgRect(s, s, bg) +
    `<polygon points="${pts.trim()}" fill="${fg}"/></svg>`
  );
};

const hearts = (p) => {
  const { scale: s, fg, bg } = normParams(p);
  const u = s / 24;
  const x = (n) => r(n * u);
  return (
    svgOpen("hearts", s, s) + bgRect(s, s, bg) +
    `<path d="M${x(12)} ${x(20)} C${x(4)} ${x(13)} ${x(4)} ${x(6)} ${x(9)} ${x(6)} ` +
    `C${x(11)} ${x(6)} ${x(12)} ${x(8)} ${x(12)} ${x(9)} ` +
    `C${x(12)} ${x(8)} ${x(13)} ${x(6)} ${x(15)} ${x(6)} ` +
    `C${x(20)} ${x(6)} ${x(20)} ${x(13)} ${x(12)} ${x(20)} Z" fill="${fg}"/></svg>`
  );
};

const confetti = (p) => {
  const { scale: s, fg, bg } = normParams(p);
  const u = s / 40;
  const pc = [[8, 10, 20], [30, 8, -15], [14, 26, 40], [34, 30, 10], [22, 18, -30]];
  const bits = pc
    .map(([cx, cy, rot]) =>
      `<rect x="${r(cx * u)}" y="${r(cy * u)}" width="${r(4 * u)}" height="${r(2 * u)}" fill="${fg}" transform="rotate(${rot} ${r(cx * u)} ${r(cy * u)})"/>`
    )
    .join("");
  return svgOpen("confetti", s, s) + bgRect(s, s, bg) + bits + `</svg>`;
};

const sprinkles = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(1 + weight * 2);
  const u = s / 40;
  const pc = [[8, 12, 30], [28, 10, -20], [16, 28, 55], [32, 30, 5], [22, 20, -40]];
  const bits = pc
    .map(([cx, cy, rot]) =>
      `<path d="M${r(cx * u)} ${r(cy * u)} l${r(6 * u)} ${r(2 * u)}" stroke="${fg}" stroke-width="${sw}" stroke-linecap="round" transform="rotate(${rot} ${r(cx * u)} ${r(cy * u)})"/>`
    )
    .join("");
  return svgOpen("sprinkles", s, s) + bgRect(s, s, bg) + bits + `</svg>`;
};

const terrazzo = (p) => {
  const { scale: s, fg, bg } = normParams(p);
  const u = s / 40;
  const pc = [[9, 11, 2.4], [30, 9, 1.6], [15, 27, 2], [33, 31, 1.4], [23, 19, 1.8], [6, 32, 1.3]];
  const bits = pc
    .map(([cx, cy, rr]) => `<circle cx="${r(cx * u)}" cy="${r(cy * u)}" r="${r(rr * u)}" fill="${fg}"/>`)
    .join("");
  return svgOpen("terrazzo", s, s) + bgRect(s, s, bg) + bits + `</svg>`;
};

const blobs = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(0.8 + weight * 1.6);
  const u = s / 40;
  const x = (n) => r(n * u);
  return (
    svgOpen("blobs", s, s) + bgRect(s, s, bg) +
    `<path d="M${x(10)} ${x(8)} Q${x(18)} ${x(4)} ${x(19)} ${x(12)} Q${x(20)} ${x(19)} ${x(12)} ${x(18)} Q${x(5)} ${x(18)} ${x(10)} ${x(8)} Z" fill="none" stroke="${fg}" stroke-width="${sw}"/>` +
    `<path d="M${x(30)} ${x(26)} Q${x(37)} ${x(25)} ${x(36)} ${x(32)} Q${x(35)} ${x(37)} ${x(29)} ${x(35)} Q${x(25)} ${x(33)} ${x(30)} ${x(26)} Z" fill="${fg}"/></svg>`
  );
};

const leaves = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(0.8 + weight * 1.4);
  const u = s / 40;
  const leaf = (lx, ly, rot) => {
    const x = r(lx * u);
    const y = r(ly * u);
    const a = r(5 * u);
    const b = r(6 * u);
    return `<g transform="rotate(${rot} ${x} ${y})"><path d="M${x} ${r(y - b)} Q${r(x + a)} ${y} ${x} ${r(y + b)} Q${r(x - a)} ${y} ${x} ${r(y - b)} Z M${x} ${r(y - b * 0.6)} V${r(y + b * 0.6)}" fill="none" stroke="${fg}" stroke-width="${sw}"/></g>`;
  };
  return (
    svgOpen("leaves", s, s) + bgRect(s, s, bg) +
    leaf(10, 10, -30) + leaf(30, 8, 20) + leaf(20, 24, 45) + leaf(32, 32, -40) + `</svg>`
  );
};

const herringbone = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(1 + weight * 2.5);
  const h = r(s / 2);
  return (
    svgOpen("herringbone", s, s) + bgRect(s, s, bg) +
    `<path d="M0 0 L${h} ${h} L0 ${s}" fill="none" stroke="${fg}" stroke-width="${sw}"/>` +
    `<path d="M${h} 0 L${s} ${h} L${h} ${s}" fill="none" stroke="${fg}" stroke-width="${sw}"/></svg>`
  );
};

const basketweave = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(0.6 + weight * 1.6);
  const h = r(s / 2);
  return (
    svgOpen("basketweave", s, s) + bgRect(s, s, bg) +
    `<rect x="${r(s * 0.08)}" y="${r(s * 0.08)}" width="${r(h - s * 0.12)}" height="${r(s - s * 0.16)}" fill="none" stroke="${fg}" stroke-width="${sw}"/>` +
    `<rect x="${r(h + s * 0.04)}" y="${r(s * 0.08)}" width="${r(s - s * 0.16)}" height="${r(h - s * 0.12)}" fill="none" stroke="${fg}" stroke-width="${sw}"/></svg>`
  );
};

const brick = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(0.6 + weight * 2);
  const h = r(s / 2);
  return (
    svgOpen("brick", s, s) + bgRect(s, s, bg) +
    `<path d="M0 0 H${s} M0 ${h} H${s} M${h} 0 V${h} M0 ${h} V${s} M${s} ${h} V${s}" ` +
    `fill="none" stroke="${fg}" stroke-width="${sw}"/></svg>`
  );
};

const trellis = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(0.6 + weight * 1.6);
  const h = r(s / 2);
  return (
    svgOpen("trellis", s, s) + bgRect(s, s, bg) +
    `<path d="M0 ${h} Q${h} 0 ${s} ${h} Q${h} ${s} 0 ${h} Z" fill="none" stroke="${fg}" stroke-width="${sw}"/></svg>`
  );
};

const weave = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(0.8 + weight * 2);
  const h = r(s / 2);
  const q = r(s / 4);
  const tq = r((s * 3) / 4);
  return (
    svgOpen("weave", s, s) + bgRect(s, s, bg) +
    `<path d="M${q} 0 V${s} M${tq} 0 V${s}" stroke="${fg}" stroke-width="${sw}" opacity="0.55"/>` +
    `<path d="M0 ${q} H${s} M0 ${tq} H${s}" stroke="${fg}" stroke-width="${sw}"/></svg>`
  );
};

const dotsOffset = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const h = r(s / 2);
  const q = r(s / 4);
  const tq = r((s * 3) / 4);
  const rad = r((s / 2) * (0.1 + weight * 0.16));
  return (
    svgOpen("dotsOffset", s, s) + bgRect(s, s, bg) +
    `<circle cx="${q}" cy="${q}" r="${rad}" fill="${fg}"/>` +
    `<circle cx="${tq}" cy="${tq}" r="${rad}" fill="${fg}"/>` +
    `<circle cx="${h}" cy="${h}" r="${rad}" fill="${fg}" opacity="0"/></svg>`
  );
};

const dotsHollow = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const c = r(s / 2);
  const sw = r(0.8 + weight * 1.8);
  const rad = r((s / 2) * (0.18 + weight * 0.28));
  return (
    svgOpen("dotsHollow", s, s) + bgRect(s, s, bg) +
    `<circle cx="${c}" cy="${c}" r="${rad}" fill="none" stroke="${fg}" stroke-width="${sw}"/></svg>`
  );
};

const ticks = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(0.6 + weight * 1.6);
  const h = r(s / 2);
  return (
    svgOpen("ticks", s, s) + bgRect(s, s, bg) +
    `<path d="M${h} 0 V${r(s * 0.28)} M0 ${h} H${r(s * 0.28)}" stroke="${fg}" stroke-width="${sw}" stroke-linecap="round"/></svg>`
  );
};

const chevronBold = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(2 + weight * 4);
  const h = r(s / 2);
  return (
    svgOpen("chevronBold", s, s) + bgRect(s, s, bg) +
    `<path d="M0 ${h} L${h} 0 L${s} ${h}" fill="none" stroke="${fg}" stroke-width="${sw}" stroke-linejoin="round"/>` +
    `<path d="M0 ${s} L${h} ${h} L${s} ${s}" fill="none" stroke="${fg}" stroke-width="${sw}" stroke-linejoin="round"/></svg>`
  );
};

const dotsGrid = (p) => {
  const { scale: s, fg, bg, weight } = normParams(p);
  const sw = r(0.4 + weight * 1.2);
  const c = r(s / 2);
  const rad = r((s / 2) * (0.08 + weight * 0.12));
  return (
    svgOpen("dotsGrid", s, s) + bgRect(s, s, bg) +
    `<path d="M${s} 0 L0 0 0 ${s}" fill="none" stroke="${fg}" stroke-width="${sw}" opacity="0.5"/>` +
    `<circle cx="${c}" cy="${c}" r="${rad}" fill="${fg}"/></svg>`
  );
};

// ---- registry --------------------------------------------------------------

// Ordered list the UI renders. id is stable (stored in the data-URI marker).
// The first 12 are the original set; the rest port the deck's DL-063a vocabulary
// (45 patterns total) — all tintable via `fg`.
export const PATTERNS = [
  { id: "checkerboard", label: "Checkerboard", generator: checkerboard },
  { id: "dots", label: "Dots", generator: dots },
  { id: "dotsOffset", label: "Dots offset", generator: dotsOffset },
  { id: "dotsHollow", label: "Dots hollow", generator: dotsHollow },
  { id: "stripes", label: "Diagonal Stripes", generator: stripes },
  { id: "diagonalReverse", label: "Diagonal reverse", generator: diagonalReverse },
  { id: "verticalStripes", label: "Vertical Stripes", generator: verticalStripes },
  { id: "linesHorizontal", label: "Lines horizontal", generator: linesHorizontal },
  { id: "grid", label: "Grid", generator: grid },
  { id: "gridFine", label: "Grid fine", generator: gridFine },
  { id: "dotsGrid", label: "Dots + grid", generator: dotsGrid },
  { id: "crosshatch", label: "Crosshatch", generator: crosshatch },
  { id: "zigzag", label: "Zigzag", generator: zigzag },
  { id: "chevron", label: "Chevron", generator: chevron },
  { id: "chevronDown", label: "Chevron down", generator: chevronDown },
  { id: "chevronBold", label: "Chevron bold", generator: chevronBold },
  { id: "herringbone", label: "Herringbone", generator: herringbone },
  { id: "waves", label: "Waves", generator: waves },
  { id: "waveTall", label: "Wave tall", generator: waveTall },
  { id: "arcs", label: "Arcs", generator: arcs },
  { id: "scales", label: "Scales", generator: scales },
  { id: "seigaiha", label: "Seigaiha", generator: seigaiha },
  { id: "triangles", label: "Triangles", generator: triangles },
  { id: "trianglesAlt", label: "Triangles alt", generator: trianglesAlt },
  { id: "diamonds", label: "Diamonds", generator: diamonds },
  { id: "diamondsFilled", label: "Diamonds filled", generator: diamondsFilled },
  { id: "hexagons", label: "Hexagons", generator: hexagons },
  { id: "quatrefoil", label: "Quatrefoil", generator: quatrefoil },
  { id: "trellis", label: "Trellis", generator: trellis },
  { id: "plus", label: "Plus", generator: plus },
  { id: "crosses", label: "Crosses", generator: crosses },
  { id: "ticks", label: "Ticks", generator: ticks },
  { id: "squares", label: "Squares", generator: squares },
  { id: "nestedSquares", label: "Nested squares", generator: nestedSquares },
  { id: "brick", label: "Brick", generator: brick },
  { id: "basketweave", label: "Basketweave", generator: basketweave },
  { id: "weave", label: "Weave", generator: weave },
  { id: "rings", label: "Rings", generator: rings },
  { id: "stars", label: "Stars", generator: stars },
  { id: "hearts", label: "Hearts", generator: hearts },
  { id: "confetti", label: "Confetti", generator: confetti },
  { id: "sprinkles", label: "Sprinkles", generator: sprinkles },
  { id: "terrazzo", label: "Terrazzo", generator: terrazzo },
  { id: "blobs", label: "Blobs", generator: blobs },
  { id: "leaves", label: "Leaves", generator: leaves },
];

export const PATTERN_BY_ID = PATTERNS.reduce((acc, p) => {
  acc[p.id] = p;
  return acc;
}, {});

// Generate the SVG markup for a pattern id + params. Returns "" for unknown ids
// (defensive: never throw into a render path).
export const generatePatternSvg = (id, params) => {
  const entry = PATTERN_BY_ID[id];
  if (!entry) return "";
  return entry.generator(params || {});
};

export { normParams };
